import { useContext, useMemo } from 'react'

import { type SanityStrings } from '@data/sanity/queries/types/site'
import { type AttributeInput } from '@data/shopify/storefront/types'
import { type LineItem } from '@lib/cart/types'
import { getLinkPageUrl } from '@lib/routes'
import {
  shopifyCombinedListingIdAttribute,
  shopifyCombinedListingPassepartoutHoleSizeAttribute,
  shopifyCombinedListingProductIdsAttribute,
  shopifyCombinedListingProductUrlAttribute,
} from '@lib/shopify/attribute'
import {
  getPassepartoutHoleSizeFromArtworkSize,
  getSizeDimensionsFromString,
  getSizeString,
} from '@lib/size'
import { StringsContext } from '@lib/strings-context'
import { ProductCombinedListingContext } from './context'
import { findCombinedListingProduct } from './product'
import {
  type CombinedListingParameterMap,
  type CombinedListingProductResult,
} from './types'

/**
 * Gets combined listing ID from cart line items.
 */
const getCombinedListingIdAttributeValue = (attributes: AttributeInput[]) => {
  const combinedListingIdAttribute = attributes.find(
    (attribute) => attribute.key === shopifyCombinedListingIdAttribute,
  )

  return combinedListingIdAttribute?.value
}

/**
 * Gets combined listing product IDs from cart line items.
 */
const getCombinedListingProductIdsAttributeValue = (
  attributes: AttributeInput[],
) => {
  const combinedListingProductIdsAttribute = attributes.find(
    (attribute) => attribute.key === shopifyCombinedListingProductIdsAttribute,
  )

  if (!combinedListingProductIdsAttribute?.value) {
    return
  }

  return combinedListingProductIdsAttribute.value
    .split(',')
    .map((productId) => Number(productId))
}

/**
 * Gets combined listing product URL from cart line items.
 */
const getCombinedListingProductUrlAttributeValue = (
  attributes: AttributeInput[],
) => {
  const combinedListingProductUrlAttribute = attributes.find(
    (attribute) => attribute.key === shopifyCombinedListingProductUrlAttribute,
  )

  return combinedListingProductUrlAttribute?.value
}

/**
 * Gets cart line items with combined listing details.
 */
export const getLineItemsWithCombinedListingDetails = (
  lineItems: LineItem[],
) => {
  return lineItems.map((lineItem) => {
    const combinedListingId = getCombinedListingIdAttributeValue(
      lineItem.attributes ?? [],
    )
    const combinedListingProductIds =
      getCombinedListingProductIdsAttributeValue(lineItem.attributes ?? [])

    if (!combinedListingId || !combinedListingProductIds) {
      return lineItem
    }

    // Options based on public attributes
    const options = [...lineItem.options]
    lineItem.attributes?.forEach((attribute) => {
      if (attribute.key.startsWith('_') || !attribute.key || !attribute.value) {
        return
      }

      options.push({
        name: attribute.key,
        value: attribute.value,
        position: options.length + 1,
      })
    })

    // Product URL
    const productUrl = getCombinedListingProductUrlAttributeValue(
      lineItem.attributes ?? [],
    )

    const newLineItem: LineItem = {
      ...lineItem,
      options,
      productUrl,
    }

    return newLineItem
  })
}

/**
 * Gets product cart attrbiutes from combined listing product result.
 */
const getCombinedListingProductCartAttributes = (
  strings: SanityStrings,
  result: CombinedListingProductResult,
  artworkSize?: string,
) => {
  switch (result.type) {
    case 'backboard': {
      const attributeInputs: AttributeInput[] = [
        {
          key: strings.cartBackboardAttribute,
          value:
            result.product.combinedListingOptionTitle ?? result.product.title,
        },
      ]

      return attributeInputs
    }

    case 'glass': {
      const attributeInputs: AttributeInput[] = [
        {
          key: strings.cartGlassAttribute,
          value:
            result.product.combinedListingOptionTitle ?? result.product.title,
        },
      ]

      return attributeInputs
    }

    case 'passepartout': {
      const attributeInputs: AttributeInput[] = [
        {
          key: strings.cartPassepartoutAttribute,
          value:
            result.product.combinedListingOptionTitle ?? result.product.title,
        },
      ]

      if (artworkSize) {
        // Calculate passepartout cut-out size
        const [artworkWidth, artworkHeight] =
          getSizeDimensionsFromString(artworkSize)
        const [passepartoutHoleWidth, passepartoutHoleHeight] =
          getPassepartoutHoleSizeFromArtworkSize(artworkWidth, artworkHeight)
        const passepartoutHoleSize = getSizeString(
          passepartoutHoleWidth,
          passepartoutHoleHeight,
          {
            decimalSeparator: '.',
          },
        )

        attributeInputs.push({
          key: shopifyCombinedListingPassepartoutHoleSizeAttribute,
          value: passepartoutHoleSize,
        })
        attributeInputs.push({
          key: strings.cartPassepartoutHoleSizeAttribute,
          value: passepartoutHoleSize,
        })
      }

      return attributeInputs
    }

    case 'spacer': {
      const attributeInputs: AttributeInput[] = [
        {
          key: strings.cartSpacerAttribute,
          value:
            result.product.combinedListingOptionTitle ?? result.product.title,
        },
      ]

      return attributeInputs
    }
  }
}

/**
 * Returns a hook that gets cart attributes for combined listing products.
 */
export const useCombinedListingCartAttributes = () => {
  const {
    combinedListingConfiguration,
    combinedListingProducts,
    combinedListingFrameSize,
    combinedListingArtworkSize,
  } = useContext(ProductCombinedListingContext)
  const strings = useContext(StringsContext)

  return useMemo(() => {
    const attributes: AttributeInput[] = []

    // When combined listing consists of only 1 variant, don't add attributes
    if (
      !combinedListingConfiguration ||
      !combinedListingProducts ||
      !combinedListingFrameSize ||
      combinedListingProducts.length === 1
    ) {
      return attributes
    }

    const combinedListingActiveProductIds = combinedListingProducts.map(
      (product) => product.productID,
    )
    const frameProduct = combinedListingProducts.find(
      (product) => product.combinedListingProductType === 'frame',
    )

    // Product attributes
    attributes.push({
      key: shopifyCombinedListingIdAttribute,
      value: combinedListingConfiguration._id,
    })
    attributes.push({
      key: shopifyCombinedListingProductIdsAttribute,
      value: combinedListingActiveProductIds.join(','),
    })
    attributes.push({
      key: strings.cartArtworkSizeAttribute,
      value: combinedListingArtworkSize ?? combinedListingFrameSize,
    })

    if (frameProduct) {
      const combinedListingUrlParameters: CombinedListingParameterMap = {
        products: combinedListingActiveProductIds.join(','),
        frame_size: combinedListingFrameSize,
        artwork_size: combinedListingArtworkSize ?? null,
      }
      const combinedListingProductUrl = getLinkPageUrl({
        pageType: 'product',
        slugs: frameProduct.slug.current,
        parameters: combinedListingUrlParameters,
      })

      attributes.push({
        key: shopifyCombinedListingProductUrlAttribute,
        value: combinedListingProductUrl,
      })
    }

    combinedListingProducts.forEach((product) => {
      const result = findCombinedListingProduct(
        combinedListingConfiguration,
        product.productID,
      )

      if (!result) {
        return
      }

      const combinedListingProductCartAttributes =
        getCombinedListingProductCartAttributes(
          strings,
          result,
          combinedListingArtworkSize ?? undefined,
        )
      combinedListingProductCartAttributes?.forEach((attribute) => {
        attributes.push(attribute)
      })
    })

    return attributes
  }, [
    combinedListingArtworkSize,
    combinedListingConfiguration,
    combinedListingFrameSize,
    combinedListingProducts,
    strings,
  ])
}
